import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  IQuestionOption,
  IReference,
  IMessage,
  INlpResponse,
  ITopic,
  ITopicGroup,
  ITopicTag,
  MessageType,
  ReferenceType,
  TNullable,
  TopicStatus,
} from '../common/types';
import { CURRENT_INSTANCE } from '../tenant';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function getTopicSearchTerms(topic: ITopic) {
  return [topic.displayName, ...(topic.keywords ?? [])];
}

export function getExactTopicFromTopicsList(
  topicsList: ITopic[],
  search: string
): TNullable<ITopic> {
  return (
    topicsList.find((topic) =>
      getTopicSearchTerms(topic).some((term) => normalizeQueryForTopicMatching(term) === search)
    ) ?? null
  );
}

export function getExactGroupFromTopicGroupsList(
  topicGroups: ITopicGroup[],
  search: string
): TNullable<ITopicGroup> {
  const foundTopicGroupByName = topicGroups.find(
    (topicGroup) => normalizeQueryForTopicMatching(topicGroup.name) === search
  );
  if (foundTopicGroupByName) return foundTopicGroupByName;

  return (
    topicGroups.find((topicGroup) =>
      topicGroup.synonyms?.some((synonym) => normalizeQueryForTopicMatching(synonym) === search)
    ) ?? null
  );
}

export function getExactTagFromTopicTagsList(
  topicTags: ITopicTag[],
  search: string
): TNullable<ITopicTag> {
  return (
    topicTags.find((topicTag) => normalizeQueryForTopicMatching(topicTag.name) === search) ?? null
  );
}

export function getOnlyDisabledTopicFromGroup(
  topics: ITopic[],
  topicGroup: TNullable<ITopicGroup>
): TNullable<ITopic> {
  if (!topicGroup) {
    return null;
  }

  const topicsInGroup = topics.filter((topic) => topic.groupId === topicGroup.id);
  if (topicsInGroup.length === 0) {
    return null;
  }

  return topicsInGroup.every((topic) => topic.status === TopicStatus.Disabled)
    ? topicsInGroup[0]
    : null;
}

export function getOnlyDisabledTopicFromTag(
  topics: ITopic[],
  topicTag: TNullable<ITopicTag>
): TNullable<ITopic> {
  if (!topicTag) {
    return null;
  }

  const topicsWithTag = topics.filter((topic) => topic.tags.some((tag) => tag.id === topicTag.id));
  if (topicsWithTag.length === 0) {
    return null;
  }

  return topicsWithTag.every((topic) => topic.status === TopicStatus.Disabled)
    ? topicsWithTag[0]
    : null;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeQueryForTermBoundaryMatching(query: string) {
  return trimQuery(query)
    .toLocaleLowerCase()
    .replace(/[\p{P}\p{S}]+/gu, ' ')
    .replace(/\s+/g, ' ');
}

function includesSearchTerm(query: string, search: string) {
  const normalizedSearch = normalizeQueryForTopicMatching(search);
  if (!normalizedSearch) {
    return false;
  }

  const searchPattern = escapeRegExp(normalizedSearch).replace(/\s+/g, '\\s+');
  const searchTermPattern = new RegExp(
    `(^|[^\\p{L}\\p{N}])${searchPattern}(?=$|[^\\p{L}\\p{N}])`,
    'u'
  );
  return (
    searchTermPattern.test(normalizeQueryForTopicMatching(query)) ||
    searchTermPattern.test(normalizeQueryForTermBoundaryMatching(query))
  );
}

export function findFirstTopicInQuery(query: string, topics: ITopic[]) {
  let matchedTopic: ITopic | undefined;
  let matchedKeywordLength = 0;

  for (const topic of topics) {
    for (const term of getTopicSearchTerms(topic)) {
      const normalizedTerm = normalizeQueryForTopicMatching(term);
      if (includesSearchTerm(query, term)) {
        if (normalizedTerm.length > matchedKeywordLength) {
          matchedTopic = topic;
          matchedKeywordLength = normalizedTerm.length;
        }
      }
    }
  }

  return matchedTopic;
}

export function findFirstTopicGroupInQuery(query: string, topicGroups: ITopicGroup[]) {
  for (const topicGroup of topicGroups) {
    if (includesSearchTerm(query, topicGroup.name)) {
      return topicGroup;
    }
  }
  for (const topicGroup of topicGroups) {
    if (topicGroup.synonyms?.length > 0) {
      for (const synonym of topicGroup.synonyms) {
        if (includesSearchTerm(query, synonym)) {
          return topicGroup;
        }
      }
    }
  }
}

export function findFirstTopicTagInQuery(query: string, topicTags: ITopicTag[]) {
  for (const topicTag of topicTags) {
    if (includesSearchTerm(query, topicTag.name)) {
      return topicTag;
    }
  }
}

export type TTopicSelectionMatch =
  | { type: 'tag'; value: ITopicTag }
  | { type: 'group'; value: ITopicGroup }
  | { type: 'topic'; value: ITopic };

export function findBestTopicSelectionMatchInQuery(
  query: string,
  topics: ITopic[],
  topicGroups: ITopicGroup[],
  topicTags: ITopicTag[]
): TTopicSelectionMatch | undefined {
  const firstTopicTag = findFirstTopicTagInQuery(query, topicTags);
  const firstTopicGroup = findFirstTopicGroupInQuery(query, topicGroups);
  const firstTopic = findFirstTopicInQuery(query, topics);
  const candidates: Array<{ match: TTopicSelectionMatch; terms: string[] }> = [];

  if (firstTopicTag) {
    candidates.push({
      match: { type: 'tag', value: firstTopicTag },
      terms: [firstTopicTag.name],
    });
  }
  if (firstTopicGroup) {
    candidates.push({
      match: { type: 'group', value: firstTopicGroup },
      terms: [firstTopicGroup.name, ...(firstTopicGroup.synonyms ?? [])],
    });
  }
  if (firstTopic) {
    candidates.push({
      match: { type: 'topic', value: firstTopic },
      terms: getTopicSearchTerms(firstTopic),
    });
  }

  let bestMatch: TTopicSelectionMatch | undefined;
  let bestMatchLength = 0;

  for (const candidate of candidates) {
    const matchLength = candidate.terms.reduce((longestLength, term) => {
      if (!includesSearchTerm(query, term)) {
        return longestLength;
      }
      return Math.max(longestLength, normalizeQueryForTopicMatching(term).length);
    }, 0);

    if (matchLength > bestMatchLength) {
      bestMatch = candidate.match;
      bestMatchLength = matchLength;
    }
  }

  return bestMatch;
}

export function trimQuery(query: string) {
  return query.trim();
}

export function normalizeQueryForTopicMatching(query: string) {
  return trimQuery(query)
    .toLocaleLowerCase()
    .replace(/\p{Pd}+/gu, ' ')
    .replace(/[\p{P}\p{S}]+/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function convertQueryToMessage(query: string, total: number): IMessage {
  const id = ++total;
  return {
    id,
    message: query,
    type: MessageType.UserEntry,
  };
}

export function questionOptionToMessage(
  question: IQuestionOption,
  questionOptions: IQuestionOption[],
  queryId: string,
  total: number
): IMessage {
  const type = question.status === 'infoservice' ? MessageType.ForceMedInfo : MessageType.Response;
  return {
    id: total,
    questionQuery: question.actualQuery,
    questionId: question.questionId,
    message: question.answer || '',
    queryId,
    type,
    references: question.references || [],
    choices: questionOptions,
  };
}

export function convertResponseToMessage(response: INlpResponse, total: number): IMessage {
  if (!response?.questionOptions) {
    return {
      id: total,
      message: 'Es ist ein Fehler aufgetreten.',
      type: MessageType.Error,
    };
  }

  const firstOption = response.questionOptions[0];

  if (!firstOption) {
    return {
      id: total,
      message: 'Keine passende Antwort gefunden.',
      type: MessageType.NoAnswerResponse,
    };
  }

  if (firstOption.optionType === 'CERTAIN_OPTION') {
    return questionOptionToMessage(
      firstOption,
      response.questionOptions,
      response.query!.id,
      total
    );
  }

  return {
    id: total,
    message: 'Ich bin mir nicht sicher. Meinten Sie eine dieser Fragen?',
    choices: response.questionOptions,
    type: MessageType.ChoiceResponse,
  };
}

export function shouldUseSessionStorage() {
  return CURRENT_INSTANCE.consentStorage === 'session';
}

export function shouldUseMaxHeight() {
  return CURRENT_INSTANCE.useMaxHeight;
}

export function shouldDisableSearchFieldAutoFocus() {
  return Boolean(CURRENT_INSTANCE.disableSearchFieldAutoFocus);
}

export function isNovartisInstance() {
  return CURRENT_INSTANCE.id === 'novartis';
}

export function buildNovartisContactFormUrl(query: string) {
  const searchParams = new URLSearchParams({
    message: trimQuery(query),
  });

  return `https://klinischeforschung.novartis.de/infoservice/?${searchParams.toString()}#modal-kontaktformular`;
}

export function getNocacheDate(date = new Date()): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}${month}${day}`;
}

export function addNocacheToUrl(url: string, date = new Date()): string {
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return url;
  }

  const hashIndex = trimmedUrl.indexOf('#');
  const urlWithoutHash = hashIndex === -1 ? trimmedUrl : trimmedUrl.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : trimmedUrl.slice(hashIndex);
  const queryIndex = urlWithoutHash.indexOf('?');
  const baseUrl = queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : urlWithoutHash.slice(queryIndex + 1);
  const searchParams = new URLSearchParams(query);

  searchParams.set('nocache', getNocacheDate(date));

  return `${baseUrl}?${searchParams.toString()}${hash}`;
}

export function getExternalDocumentLink(reference: IReference): string {
  if (reference.referenceType !== ReferenceType.Smpc) {
    return reference.externalDocumentLink;
  }

  return addNocacheToUrl(reference.externalDocumentLink);
}

export const QUERY_PARAM = 'query';
export const SUBMIT_QUERY_PARAM = 'submitQuery';
